import React, { useState, useEffect } from 'react';
import { Wallet, TrendingUp, TrendingDown, ArrowRightLeft, Plus, RefreshCw, Ban, X } from 'lucide-react';
import axios from 'axios';
import Pagination from '@/components/Pagination';

const CashRegister = () => {
  const [cashBalance, setCashBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [voidLoading, setVoidLoading] = useState(false);
  const [depositData, setDepositData] = useState({
    amount: '',
    bank_id: '',
    deposit_date: new Date().toISOString().split('T')[0],
    reference_number: '',
    notes: ''
  });
  const [filters, setFilters] = useState({
    from_date: '', // Empty for all time by default
    to_date: new Date().toISOString().split('T')[0], // Today
    transaction_type: 'all'
  });
  const [summary, setSummary] = useState({ total_receipts: 0, total_payments: 0, balance: 0 });
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchCashBalance();
    fetchBanks();
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [currentPage, itemsPerPage]);

  const fetchCashBalance = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/cash-register/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCashBalance(response.data);
    } catch (error) {
      console.error('Error fetching cash balance:', error);
    }
  };

  const fetchBanks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/accounts/banks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBanks(response.data);
    } catch (error) {
      console.error('Error fetching banks:', error);
    }
  };

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.from_date) params.append('from_date', filters.from_date);
      if (filters.to_date) params.append('to_date', filters.to_date);
      if (filters.transaction_type !== 'all') params.append('transaction_type', filters.transaction_type);
      params.append('page', currentPage);
      params.append('limit', itemsPerPage);

      const response = await axios.get(`${API_URL}/api/cash-register/transactions?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Handle paginated response
      setTransactions(response.data.transactions || []);
      setTotalItems(response.data.total || response.data.transactions?.length || 0);
      setSummary(response.data.summary || { total_receipts: 0, total_payments: 0, balance: 0 });
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleFilterApply = () => {
    setCurrentPage(1);
    fetchTransactions();
  };

  const handleVoidClick = (txn) => {
    setVoidTarget(txn);
    setVoidReason('');
    setShowVoidModal(true);
  };

  const handleVoidSubmit = async () => {
    if (!voidReason.trim()) {
      alert('Please provide a reason for voiding this transaction.');
      return;
    }
    setVoidLoading(true);
    try {
      await axios.put(`${API_URL}/api/cash-register/void/${voidTarget.id}`, {
        reason: voidReason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Transaction voided successfully');
      setShowVoidModal(false);
      setVoidTarget(null);
      setVoidReason('');
      fetchTransactions();
      fetchCashBalance();
    } catch (error) {
      console.error('Error voiding transaction:', error);
      alert(error.response?.data?.detail || 'Failed to void transaction');
    } finally {
      setVoidLoading(false);
    }
  };

  // Quick date range filters
  const setDateRange = (range) => {
    const today = new Date();
    let fromDate, toDate;

    switch(range) {
      case 'today':
        fromDate = toDate = today.toISOString().split('T')[0];
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        fromDate = toDate = yesterday.toISOString().split('T')[0];
        break;
      case 'this_week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        fromDate = weekStart.toISOString().split('T')[0];
        toDate = today.toISOString().split('T')[0];
        break;
      case 'this_month':
        fromDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        toDate = today.toISOString().split('T')[0];
        break;
      case 'last_month':
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        fromDate = lastMonth.toISOString().split('T')[0];
        toDate = lastMonthEnd.toISOString().split('T')[0];
        break;
      case 'this_year':
        fromDate = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
        toDate = today.toISOString().split('T')[0];
        break;
      case 'all_time':
        fromDate = '';
        toDate = '';
        break;
      default:
        return;
    }

    setFilters(prev => ({ ...prev, from_date: fromDate, to_date: toDate }));
  };

  const handleDepositToBankSubmit = async (e) => {
    e.preventDefault();
    
    if (!depositData.bank_id || !depositData.amount || parseFloat(depositData.amount) <= 0) {
      alert('Please select a bank and enter a valid amount');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/cash-register/deposit-to-bank`, {
        ...depositData,
        amount: parseFloat(depositData.amount)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('Cash deposit recorded successfully!');
      setShowDepositModal(false);
      setDepositData({
        amount: '',
        bank_id: '',
        deposit_date: new Date().toISOString().split('T')[0],
        reference_number: '',
        notes: ''
      });
      fetchCashBalance();
      fetchTransactions();
    } catch (error) {
      console.error('Error depositing cash:', error);
      alert('Failed to record cash deposit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cash Register</h1>
          <p className="text-sm text-gray-500 mt-1">Track cash transactions and balances</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => {
              fetchCashBalance();
              fetchTransactions();
            }}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
          <button
            onClick={() => setShowDepositModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Deposit to Bank
          </button>
        </div>
      </div>

      {/* Cash Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-lg shadow-lg text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-100">Current Cash Balance</p>
              <p className="text-3xl font-bold mt-1">
                ₹{cashBalance ? cashBalance.cash_balance.toLocaleString('en-IN') : '0'}
              </p>
            </div>
            <Wallet className="h-12 w-12 text-green-200" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Cash Income</p>
              <p className="text-2xl font-bold text-green-600 mt-1">
                ₹{cashBalance ? cashBalance.total_cash_income.toLocaleString('en-IN') : '0'}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {cashBalance?.income_count || 0} transactions
              </p>
            </div>
            <TrendingUp className="h-10 w-10 text-green-500" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Cash Expenses</p>
              <p className="text-2xl font-bold text-red-600 mt-1">
                ₹{cashBalance ? cashBalance.total_cash_expenses.toLocaleString('en-IN') : '0'}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {cashBalance?.expense_count || 0} transactions
              </p>
            </div>
            <TrendingDown className="h-10 w-10 text-red-500" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Cash Deposits to Bank</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">
                ₹{cashBalance ? cashBalance.total_cash_deposits.toLocaleString('en-IN') : '0'}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {cashBalance?.deposit_count || 0} deposits
              </p>
            </div>
            <ArrowRightLeft className="h-10 w-10 text-blue-500" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-lg font-semibold mb-4">Filter Transactions</h3>
        
        {/* Quick Filter Buttons */}
        <div className="mb-4">
          <p className="text-sm font-medium text-gray-700 mb-2">Quick Filters:</p>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setDateRange('all_time')} className="px-3 py-1 text-sm bg-green-500 text-white rounded hover:bg-green-600 font-medium">All Time</button>
            <button onClick={() => setDateRange('today')} className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200">Today</button>
            <button onClick={() => setDateRange('yesterday')} className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200">Yesterday</button>
            <button onClick={() => setDateRange('this_week')} className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200">This Week</button>
            <button onClick={() => setDateRange('this_month')} className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200">This Month</button>
            <button onClick={() => setDateRange('last_month')} className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200">Last Month</button>
            <button onClick={() => setDateRange('this_year')} className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200">This Year</button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
            <input
              type="date"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.from_date}
              onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
            <input
              type="date"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.to_date}
              onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
            <select
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.transaction_type}
              onChange={(e) => setFilters({ ...filters, transaction_type: e.target.value })}
            >
              <option value="all">All Transactions</option>
              <option value="income">Income Only</option>
              <option value="expense">Expense Only</option>
              <option value="deposit">Deposits Only</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={handleFilterApply}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Apply Filters'}
            </button>
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold">Cash Transactions</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {transactions.map((txn, index) => {
                const isVoided = txn.status === 'Voided';
                return (
                <tr key={txn.id || index} className={`hover:bg-gray-50 ${isVoided ? 'bg-red-50 opacity-60' : ''}`}>
                  <td className={`px-4 py-3 text-sm ${isVoided ? 'line-through text-gray-400' : ''}`}>{txn.date}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      isVoided ? 'bg-gray-100 text-gray-500' :
                      txn.type === 'income' ? 'bg-green-100 text-green-800' :
                      txn.type === 'expense' ? 'bg-red-100 text-red-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {txn.type}
                    </span>
                  </td>
                  <td className={`px-4 py-3 text-sm ${isVoided ? 'line-through text-gray-400' : ''}`}>{txn.category}</td>
                  <td className={`px-4 py-3 text-sm ${isVoided ? 'line-through text-gray-400' : 'text-gray-600'}`}>
                    {txn.description}
                    {isVoided && txn.voided_reason && (
                      <p className="text-xs text-red-500 mt-1 no-underline" style={{textDecoration: 'none'}}>Void reason: {txn.voided_reason}</p>
                    )}
                  </td>
                  <td className={`px-4 py-3 text-sm text-right font-semibold ${
                    isVoided ? 'line-through text-gray-400' :
                    txn.amount >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    ₹{Math.abs(txn.amount).toLocaleString('en-IN')}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {isVoided ? (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">Voided</span>
                    ) : (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Active</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {!isVoided && txn.id && (
                      <button
                        onClick={() => handleVoidClick(txn)}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Void this transaction"
                        data-testid={`void-btn-${txn.id}`}
                      >
                        <Ban className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          {transactions.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No transactions found
            </div>
          )}
        </div>
        
        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={Math.ceil(totalItems / itemsPerPage)}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      </div>

      {/* Deposit to Bank Modal */}
      {showDepositModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">Deposit Cash to Bank</h3>
            <form onSubmit={handleDepositToBankSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount *
                  </label>
                  <input
                    type="number"
                    value={depositData.amount}
                    onChange={(e) => setDepositData({ ...depositData, amount: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter amount"
                    step="0.01"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Bank *
                  </label>
                  <select
                    value={depositData.bank_id}
                    onChange={(e) => setDepositData({ ...depositData, bank_id: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">-- Select Bank --</option>
                    {banks.map(bank => (
                      <option key={bank.id} value={bank.id}>
                        {bank.bank_name} - {bank.account_number}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Deposit Date
                  </label>
                  <input
                    type="date"
                    value={depositData.deposit_date}
                    onChange={(e) => setDepositData({ ...depositData, deposit_date: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reference Number
                  </label>
                  <input
                    type="text"
                    value={depositData.reference_number}
                    onChange={(e) => setDepositData({ ...depositData, reference_number: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Deposit slip number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    value={depositData.notes}
                    onChange={(e) => setDepositData({ ...depositData, notes: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    placeholder="Additional notes"
                  />
                </div>
              </div>

              <div className="flex gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => setShowDepositModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {loading ? 'Processing...' : 'Record Deposit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Void Confirmation Modal */}
      {showVoidModal && voidTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-red-700">Void Transaction</h3>
              <button onClick={() => setShowVoidModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-800 font-medium mb-2">You are about to void this transaction:</p>
              <div className="text-sm text-red-700 space-y-1">
                <p><strong>Date:</strong> {voidTarget.date}</p>
                <p><strong>Category:</strong> {voidTarget.category}</p>
                <p><strong>Description:</strong> {voidTarget.description}</p>
                <p><strong>Amount:</strong> ₹{Math.abs(voidTarget.amount).toLocaleString('en-IN')}</p>
              </div>
              <p className="text-xs text-red-600 mt-2">This entry will be excluded from balance calculations but will remain visible in the register for audit purposes.</p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for voiding *
              </label>
              <textarea
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                rows="3"
                placeholder="E.g., Duplicate entry - student deleted, Incorrect amount, etc."
                required
                data-testid="void-reason-input"
              />
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowVoidModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleVoidSubmit}
                disabled={voidLoading || !voidReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400"
                data-testid="confirm-void-btn"
              >
                {voidLoading ? 'Voiding...' : 'Void Transaction'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CashRegister;
